import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import {
  CurrencyType,
  getCurrencyDetails,
} from '../../../../constants/currencies';
import useIsPortrait from '../../../../hooks/useIsPortrait';
import { useSessionStore } from '../../../../stores/SessionStore';
import { useUserStore } from '../../../../stores/UserStore';
import { AssetDisplayBar, currencyAmonuts } from '../../AssetDisplayBar';
import { CountDownText } from '../../CountDownText';
import { PngIcon } from '../../PngIcon';

import './GeneralModal.css';

export enum GeneralModalYesButtonVariant {
  Cyan = 'cyan',
  Red = 'red',
  WEABlue = 'WEABlue',
  cyanDisabled = 'cyanDisabled',
  WEAblueDisabled = 'WEAblueDisabled',
}

export enum GeneralModalNoButtonVariant {
  Cyan = 'cyan',
  Gray = 'gray',
  WEABlue = 'WEABlue',
}

interface ButtonSetting {
  noText: string;
  yesText?: string;
  noVariant: GeneralModalNoButtonVariant;
  yesVariant?: GeneralModalYesButtonVariant;
  yesFee?: {
    type: CurrencyType;
    amount: number;
  };
  yesCountDown?: number;
}

type GeneralModalProps = {
  title?: string;
  warning?: string;
  subTitle?: string;
  hasDivider?: boolean;
  content?: string;
  children?: React.ReactNode;
  buttonSetting?: ButtonSetting;
  isYesNo?: boolean;
  currencies?: CurrencyType[];
  isDisableBackground?: boolean;
  isDisableWidthAndPadding?: boolean;
  onYes?: () => void;
  onClose: () => void;
  onBackdropClick?: () => void;
};

export const GeneralModal = memo(
  ({
    title,
    warning,
    subTitle,
    hasDivider,
    content,
    children,
    buttonSetting,
    isYesNo,
    currencies = [],
    isDisableBackground,
    isDisableWidthAndPadding = false, // seems many popups still relie on this old styling
    onYes,
    onClose,
    onBackdropClick,
  }: GeneralModalProps) => {
    const isPortrait = useIsPortrait();

    const userId = useSessionStore((store) => store.activeUserId);

    const userProfile = useUserStore((store) => store.getProfile(userId ?? ''));
    const userAccount = useUserStore((store) => store.getAccount(userId ?? ''));
    const allowance = useUserStore((store) => store.getAllowance());

    const [disabled, setDisabled] = useState(
      buttonSetting?.yesCountDown ? true : false,
    );

    const onTimeout = () => {
      // Enable the button when the time is up
      setDisabled(false);
    };

    const handleBackdropClick = useCallback(() => {
      if (onBackdropClick) {
        onBackdropClick();
      } else {
        onClose();
      }
    }, [onBackdropClick, onClose]);

    const makePrimaryButtonLabel = () => {
      if (buttonSetting?.yesCountDown) {
        return (
          <CountDownText
            timestamp={buttonSetting.yesCountDown}
            label={buttonSetting?.yesText}
            className="yes-countdown"
            onTimeout={onTimeout}
          />
        );
      }
      if (!buttonSetting?.yesFee) {
        return <p className="yesButton-text">{buttonSetting?.yesText}</p>;
      }

      const { icon } = getCurrencyDetails(buttonSetting.yesFee.type);

      return (
        <>
          {buttonSetting?.yesText ? (
            <p className="yesButton-text">{buttonSetting?.yesText}</p>
          ) : null}
          <div className={'yes-fee-container'}>
            <PngIcon name={icon} width={24} height={24} />
            <span>{buttonSetting.yesFee.amount}</span>
          </div>
        </>
      );
    };

    const assetCurrencies = useMemo(
      () =>
        currencyAmonuts(
          {
            gameStamina: allowance?.allowances.webPlan.quantities.token,
            creationStamina:
              allowance?.allowances.webPlan.quantities.creationToken,
            token: userAccount?.balance.token,
          },
          currencies,
        ),
      [
        currencies,
        allowance?.allowances.webPlan.quantities.creationToken,
        allowance?.allowances.webPlan.quantities.token,
        userAccount?.balance.token,
      ],
    );

    useEffect(() => {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }, []);

    return (
      <div
        className={`general-modal ${
          isDisableBackground ? 'disable-background' : ''
        }`}
        onClick={handleBackdropClick}
      >
        {assetCurrencies.length > 0 ? (
          <div className={'asset-display-bar'}>
            <AssetDisplayBar
              profile={userProfile}
              currencies={assetCurrencies}
            />
          </div>
        ) : null}
        <div
          className={`modal-container  ${isPortrait ? 'isPortrait' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="modal-text-container"
            style={isDisableWidthAndPadding ? {} : { padding: '0 0.5rem' }}
          >
            {title && (
              <div className="title-container">
                <p className="title">{title}</p>
              </div>
            )}

            {warning && (
              <div className="warning-container">
                <p className="warning">{warning}</p>
              </div>
            )}

            {subTitle && (
              <div className="subTitle-container">
                <p className="subTitle">{subTitle}</p>
              </div>
            )}

            {hasDivider && <hr />}

            {content && (
              <div className="content-container">
                <p className="content">{content}</p>
              </div>
            )}

            {children && (
              <div
                className="children-container"
                style={
                  isDisableWidthAndPadding
                    ? {}
                    : { width: '96%', padding: '4px' }
                }
              >
                {children}
              </div>
            )}
          </div>

          {buttonSetting && (
            <div className="button-container">
              <div
                className={`noButton-container ${buttonSetting.noVariant} button-container`}
                onClick={onClose}
              >
                <p className={`noButton-text ${buttonSetting.noVariant}`}>
                  {buttonSetting.noText}
                </p>
              </div>

              {isYesNo && (
                <div
                  className={`yesButton-container ${buttonSetting.yesVariant} button-container`}
                  onClick={disabled ? undefined : onYes}
                >
                  {disabled && <div className="yesButton-blackShadow" />}
                  {makePrimaryButtonLabel()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);
